import React, { useState } from 'react';
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar } from 'recharts';
import '../../App.css';

const EvoMLGovernance = () => {
  const [activeTab, setActiveTab] = useState('overview');

  // Data for visualizations
  const complianceScore = [
    { category: 'Data Lineage', score: 95, fullScore: 100 },
    { category: 'Model Versioning', score: 98, fullScore: 100 },
    { category: 'Audit Trail', score: 92, fullScore: 100 },
    { category: 'Reproducibility', score: 96, fullScore: 100 },
    { category: 'Explainability', score: 88, fullScore: 100 },
    { category: 'Access Control', score: 94, fullScore: 100 }
  ];

  const interoperabilityFormats = [
    { name: 'Python', value: 100, color: '#3776AB' },
    { name: 'ONNX', value: 95, color: '#005A9C' },
    { name: 'PMML', value: 85, color: '#FF6B6B' },
    { name: 'Docker', value: 100, color: '#2496ED' },
    { name: 'REST API', value: 100, color: '#4CAF50' },
    { name: 'MLflow', value: 98, color: '#0194E2' }
  ];

  const auditTrailExample = [
    { timestamp: '2024-01-15 10:30', user: 'data_scientist_1', action: 'Dataset uploaded', details: 'customer_churn_2024.csv' },
    { timestamp: '2024-01-15 11:15', user: 'evoml_system', action: 'Data validation completed', details: '15,234 records validated' },
    { timestamp: '2024-01-15 11:45', user: 'evoml_system', action: 'Feature engineering', details: '47 features generated' },
    { timestamp: '2024-01-15 14:20', user: 'ml_engineer_2', action: 'Model training started', details: 'XGBoost, Random Forest, Neural Net' },
    { timestamp: '2024-01-15 16:30', user: 'evoml_system', action: 'Best model selected', details: 'XGBoost - AUC: 0.92' },
    { timestamp: '2024-01-15 17:00', user: 'compliance_officer', action: 'Model approved', details: 'Passed bias checks' }
  ];

  const TabButton = ({ id, label, icon }) => (
    <button
      onClick={() => setActiveTab(id)}
      style={{
        padding: '12px 24px',
        background: activeTab === id ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : '#f0f0f0',
        color: activeTab === id ? 'white' : '#333',
        border: 'none',
        borderRadius: '8px',
        cursor: 'pointer',
        fontSize: '14px',
        fontWeight: '600',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        transition: 'all 0.3s ease',
        boxShadow: activeTab === id ? '0 4px 15px rgba(102, 126, 234, 0.3)' : 'none'
      }}
    >
      <span style={{ fontSize: '18px' }}>{icon}</span>
      {label}
    </button>
  );

  const FeatureCard = ({ title, icon, children }) => (
    <div style={{
      background: 'white',
      borderRadius: '12px',
      padding: '24px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
      marginBottom: '20px'
    }}>
      <h3 style={{
        margin: '0 0 20px 0',
        fontSize: '20px',
        color: '#333',
        display: 'flex',
        alignItems: 'center',
        gap: '10px'
      }}>
        <span style={{ fontSize: '24px' }}>{icon}</span>
        {title}
      </h3>
      {children}
    </div>
  );

  return (
    <div style={{
      minHeight: '100vh',
      background: '#f8f9fa',
      padding: '40px 20px'
    }}>
      <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ marginBottom: '40px' }}>
          <h1 style={{
            fontSize: '36px',
            fontWeight: '700',
            color: '#1a1a1a',
            marginBottom: '10px',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent'
          }}>
            EvoML Governance & Compliance
          </h1>
          <p style={{
            fontSize: '18px',
            color: '#666',
            lineHeight: '1.6'
          }}>
            Enterprise-grade auditability, traceability, and interoperability for responsible AI deployment
          </p>
        </div>

        {/* Tab Navigation */}
        <div style={{
          display: 'flex',
          gap: '12px',
          marginBottom: '30px',
          flexWrap: 'wrap'
        }}>
          <TabButton id="overview" label="Overview" icon="🏛️" />
          <TabButton id="auditability" label="Auditability" icon="📋" />
          <TabButton id="traceability" label="Traceability" icon="🔍" />
          <TabButton id="interoperability" label="Interoperability" icon="🔌" />
          <TabButton id="compliance" label="Compliance" icon="⚖️" />
        </div>

        {/* Content */}
        {activeTab === 'overview' && (
          <div>
            <FeatureCard title="Governance Overview" icon="🏛️">
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                gap: '20px',
                marginBottom: '30px'
              }}>
                {[
                  {
                    title: 'Complete Auditability',
                    icon: '📋',
                    description: 'Every action, decision, and transformation is logged with timestamps and user attribution',
                    metric: '100% Coverage'
                  },
                  {
                    title: 'End-to-End Traceability',
                    icon: '🔍',
                    description: 'Track data lineage from raw input through every transformation to final predictions',
                    metric: 'Full Pipeline'
                  },
                  {
                    title: 'Universal Interoperability',
                    icon: '🔌',
                    description: 'Export models and code in multiple formats, integrate with any ML infrastructure',
                    metric: '10+ Formats'
                  }
                ].map((feature, idx) => (
                  <div key={idx} style={{
                    background: '#f8f9fa',
                    padding: '20px',
                    borderRadius: '8px',
                    border: '1px solid #e0e0e0'
                  }}>
                    <div style={{ fontSize: '32px', marginBottom: '10px' }}>{feature.icon}</div>
                    <h4 style={{ margin: '0 0 10px 0', color: '#333' }}>{feature.title}</h4>
                    <p style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>{feature.description}</p>
                    <div style={{
                      fontSize: '24px',
                      fontWeight: 'bold',
                      color: '#667eea'
                    }}>{feature.metric}</div>
                  </div>
                ))}
              </div>

              <h4 style={{ marginBottom: '20px' }}>Compliance Score Dashboard</h4>
              <ResponsiveContainer width="100%" height={300}>
                <RadarChart data={complianceScore}>
                  <PolarGrid stroke="#e0e0e0" />
                  <PolarAngleAxis dataKey="category" tick={{ fontSize: 12 }} />
                  <PolarRadiusAxis angle={90} domain={[0, 100]} tick={{ fontSize: 10 }} />
                  <Radar name="Current Score" dataKey="score" stroke="#667eea" fill="#667eea" fillOpacity={0.6} />
                  <Tooltip />
                </RadarChart>
              </ResponsiveContainer>
            </FeatureCard>
          </div>
        )}

        {activeTab === 'auditability' && (
          <div>
            <FeatureCard title="Complete Audit Trail" icon="📋">
              <div style={{ marginBottom: '30px' }}>
                <h4 style={{ marginBottom: '15px' }}>What We Track</h4>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                  gap: '15px'
                }}>
                  {[
                    { category: 'Data Operations', items: ['Data uploads', 'Transformations', 'Feature engineering', 'Data validation'] },
                    { category: 'Model Development', items: ['Algorithm selection', 'Hyperparameter tuning', 'Training runs', 'Evaluation metrics'] },
                    { category: 'Deployment', items: ['Model versions', 'Deployment approvals', 'Configuration changes', 'Rollback events'] },
                    { category: 'Access Control', items: ['User logins', 'Permission changes', 'Data access', 'Model access'] }
                  ].map((section, idx) => (
                    <div key={idx} style={{
                      background: '#f8f9fa',
                      padding: '15px',
                      borderRadius: '8px',
                      border: '1px solid #e0e0e0'
                    }}>
                      <h5 style={{ margin: '0 0 10px 0', color: '#667eea' }}>{section.category}</h5>
                      <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '14px', color: '#666' }}>
                        {section.items.map((item, i) => (
                          <li key={i} style={{ marginBottom: '5px' }}>{item}</li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>

              <h4 style={{ marginBottom: '15px' }}>Live Audit Trail Example</h4>
              <div style={{
                background: '#f8f9fa',
                borderRadius: '8px',
                overflow: 'hidden'
              }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: '#667eea', color: 'white' }}>
                      <th style={{ padding: '12px', textAlign: 'left' }}>Timestamp</th>
                      <th style={{ padding: '12px', textAlign: 'left' }}>User</th>
                      <th style={{ padding: '12px', textAlign: 'left' }}>Action</th>
                      <th style={{ padding: '12px', textAlign: 'left' }}>Details</th>
                    </tr>
                  </thead>
                  <tbody>
                    {auditTrailExample.map((entry, idx) => (
                      <tr key={idx} style={{ borderBottom: '1px solid #e0e0e0' }}>
                        <td style={{ padding: '12px', fontSize: '14px' }}>{entry.timestamp}</td>
                        <td style={{ padding: '12px', fontSize: '14px' }}>{entry.user}</td>
                        <td style={{ padding: '12px', fontSize: '14px' }}>{entry.action}</td>
                        <td style={{ padding: '12px', fontSize: '14px' }}>{entry.details}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </FeatureCard>
          </div>
        )}

        {activeTab === 'traceability' && (
          <div>
            <FeatureCard title="End-to-End Data & Model Lineage" icon="🔍">
              <div style={{ marginBottom: '30px' }}>
                <h4 style={{ marginBottom: '15px' }}>Data Lineage Tracking</h4>
                <div style={{
                  background: '#f8f9fa',
                  padding: '20px',
                  borderRadius: '8px',
                  marginBottom: '20px'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '20px', overflowX: 'auto' }}>
                    {[
                      { step: 'Raw Data', icon: '📁', details: 'Source: S3://data-lake/raw/customers.csv' },
                      { step: 'Validated', icon: '✅', details: 'Records: 15,234 | Dropped: 156' },
                      { step: 'Transformed', icon: '🔄', details: 'Features: 47 | Engineered: 23' },
                      { step: 'Split', icon: '✂️', details: 'Train: 80% | Test: 20%' },
                      { step: 'Model Input', icon: '🤖', details: 'Shape: (12,187, 47)' }
                    ].map((stage, idx) => (
                      <div key={idx} style={{
                        minWidth: '150px',
                        textAlign: 'center'
                      }}>
                        <div style={{
                          width: '60px',
                          height: '60px',
                          borderRadius: '50%',
                          background: '#667eea',
                          color: 'white',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '24px',
                          margin: '0 auto 10px'
                        }}>
                          {stage.icon}
                        </div>
                        <h5 style={{ margin: '0 0 5px 0' }}>{stage.step}</h5>
                        <p style={{ fontSize: '12px', color: '#666' }}>{stage.details}</p>
                      </div>
                    ))}
                  </div>
                </div>

                <h4 style={{ marginBottom: '15px' }}>Model Lineage</h4>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                  gap: '20px'
                }}>
                  <div style={{
                    background: '#f8f9fa',
                    padding: '20px',
                    borderRadius: '8px',
                    border: '1px solid #e0e0e0'
                  }}>
                    <h5 style={{ margin: '0 0 15px 0', color: '#667eea' }}>📊 Model Provenance</h5>
                    <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '14px' }}>
                      <li>Model ID: mdl_20240115_xgb_v3</li>
                      <li>Parent Models: v1, v2</li>
                      <li>Training Data: dataset_20240115</li>
                      <li>Feature Set: feature_set_v3.2</li>
                      <li>Algorithm: XGBoost 1.7.3</li>
                      <li>Created By: evoml_pipeline</li>
                    </ul>
                  </div>
                  <div style={{
                    background: '#f8f9fa',
                    padding: '20px',
                    borderRadius: '8px',
                    border: '1px solid #e0e0e0'
                  }}>
                    <h5 style={{ margin: '0 0 15px 0', color: '#667eea' }}>🔧 Configuration Tracking</h5>
                    <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '14px' }}>
                      <li>Hyperparameters: Logged</li>
                      <li>Random Seeds: Fixed & Tracked</li>
                      <li>Environment: Docker Image SHA</li>
                      <li>Dependencies: requirements.txt</li>
                      <li>Git Commit: a3f8d2c</li>
                      <li>Pipeline Version: 2.1.0</li>
                    </ul>
                  </div>
                </div>
              </div>
            </FeatureCard>
          </div>
        )}

        {activeTab === 'interoperability' && (
          <div>
            <FeatureCard title="Universal Model & Code Export" icon="🔌">
              <div style={{ marginBottom: '30px' }}>
                <h4 style={{ marginBottom: '20px' }}>Supported Export Formats</h4>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={interoperabilityFormats}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="name" />
                    <YAxis domain={[0, 100]} />
                    <Tooltip />
                    <Bar dataKey="value" fill="#667eea">
                      {interoperabilityFormats.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>

              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                gap: '20px'
              }}>
                <div style={{
                  background: '#f8f9fa',
                  padding: '20px',
                  borderRadius: '8px'
                }}>
                  <h5 style={{ margin: '0 0 15px 0', color: '#3776AB' }}>🐍 Python Native</h5>
                  <p style={{ fontSize: '14px', color: '#666' }}>
                    Export models as pure Python code with sklearn-compatible API. Full compatibility with existing ML pipelines and frameworks.
                  </p>
                  <ul style={{ fontSize: '14px', color: '#666', marginTop: '10px' }}>
                    <li>Sklearn-compatible interface</li>
                    <li>No proprietary dependencies</li>
                    <li>Version-controlled exports</li>
                  </ul>
                </div>

                <div style={{
                  background: '#f8f9fa',
                  padding: '20px',
                  borderRadius: '8px'
                }}>
                  <h5 style={{ margin: '0 0 15px 0', color: '#005A9C' }}>🔄 ONNX Export</h5>
                  <p style={{ fontSize: '14px', color: '#666' }}>
                    Cross-platform deployment with ONNX format. Deploy models to any runtime environment supporting ONNX.
                  </p>
                  <ul style={{ fontSize: '14px', color: '#666', marginTop: '10px' }}>
                    <li>Hardware acceleration support</li>
                    <li>Language-agnostic deployment</li>
                    <li>Optimized inference</li>
                  </ul>
                </div>

                <div style={{
                  background: '#f8f9fa',
                  padding: '20px',
                  borderRadius: '8px'
                }}>
                  <h5 style={{ margin: '0 0 15px 0', color: '#2496ED' }}>🐳 Docker Container</h5>
                  <p style={{ fontSize: '14px', color: '#666' }}>
                    Ready-to-deploy containerized models with all dependencies included. Kubernetes-ready for enterprise deployments.
                  </p>
                  <ul style={{ fontSize: '14px', color: '#666', marginTop: '10px' }}>
                    <li>Pre-configured serving endpoints</li>
                    <li>Auto-scaling support</li>
                    <li>Health checks included</li>
                  </ul>
                </div>

                <div style={{
                  background: '#f8f9fa',
                  padding: '20px',
                  borderRadius: '8px'
                }}>
                  <h5 style={{ margin: '0 0 15px 0', color: '#4CAF50' }}>🌐 REST API</h5>
                  <p style={{ fontSize: '14px', color: '#666' }}>
                    Auto-generated REST APIs for model serving. OpenAPI documentation and client SDKs included.
                  </p>
                  <ul style={{ fontSize: '14px', color: '#666', marginTop: '10px' }}>
                    <li>Automatic API documentation</li>
                    <li>Built-in authentication</li>
                    <li>Request/response validation</li>
                  </ul>
                </div>
              </div>
            </FeatureCard>
          </div>
        )}

        {activeTab === 'compliance' && (
          <div>
            <FeatureCard title="Regulatory Compliance & Standards" icon="⚖️">
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                gap: '20px',
                marginBottom: '30px'
              }}>
                {[
                  {
                    standard: 'GDPR Compliance',
                    icon: '🇪🇺',
                    features: ['Right to explanation', 'Data deletion', 'Consent tracking', 'Privacy by design'],
                    status: 'Certified'
                  },
                  {
                    standard: 'SOC 2 Type II',
                    icon: '🔒',
                    features: ['Security controls', 'Availability', 'Confidentiality', 'Privacy'],
                    status: 'In Progress'
                  }
                ].map((compliance, idx) => (
                  <div key={idx} style={{
                    background: 'white',
                    padding: '20px',
                    borderRadius: '8px',
                    border: '1px solid #e0e0e0',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                  }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      marginBottom: '15px'
                    }}>
                      <span style={{ fontSize: '32px', marginRight: '10px' }}>{compliance.icon}</span>
                      <div>
                        <h4 style={{ margin: 0 }}>{compliance.standard}</h4>
                        <span style={{
                          background: compliance.status === 'In Progress' ? '#FF9800' : '#4CAF50',
                          color: 'white',
                          padding: '2px 8px',
                          borderRadius: '12px',
                          fontSize: '12px',
                          fontWeight: 'bold'
                        }}>{compliance.status}</span>
                      </div>
                    </div>
                    <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '14px', color: '#666' }}>
                      {compliance.features.map((feature, i) => (
                        <li key={i} style={{ marginBottom: '5px' }}>{feature}</li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>

              <h4 style={{ marginBottom: '15px' }}>Built-in Compliance Features</h4>
              <div style={{
                background: '#f8f9fa',
                padding: '20px',
                borderRadius: '8px'
              }}>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                  gap: '15px'
                }}>
                  {[
                    { feature: 'Automated Documentation', description: 'Every model comes with auto-generated documentation' },
                    { feature: 'Bias Detection', description: 'Built-in fairness metrics and bias testing' },
                    { feature: 'Explainability Reports', description: 'SHAP/LIME explanations for every prediction' },
                    { feature: 'Version Control', description: 'Git-like versioning for models and data' },
                    { feature: 'Access Controls', description: 'Role-based permissions and audit logs' },
                    { feature: 'Data Governance', description: 'PII detection and data retention policies' }
                  ].map((item, idx) => (
                    <div key={idx}>
                      <h5 style={{ margin: '0 0 5px 0', color: '#667eea' }}>{item.feature}</h5>
                      <p style={{ margin: 0, fontSize: '13px', color: '#666' }}>{item.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            </FeatureCard>
          </div>
        )}
      </div>
    </div>
  );
};

export default EvoMLGovernance; 